import type { ScreenerItem } from "@/types/screener";

import type { ProductPickerHit } from "./productPicker";

export interface PortfolioHoldingProduct {
  resource_id?: string;
  child_universe_id?: string;
  portfolio_reference_id?: string;
  display_name: string;
  long_name?: string;
  resource_type: string;
  company_id?: string;
  currency?: string;
  default_performance_stream?: string;
  isin?: string;
  pickerHit?: ProductPickerHit;
}

function readString(
  value: string | number | boolean | null | undefined,
): string | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  return String(value);
}

type ScreenerMetadataExtras = {
  long_name?: string;
  company_id?: string;
};

function readMetadataString(
  metadata: ScreenerItem["metadata"],
  field: keyof ScreenerMetadataExtras,
): string | undefined {
  if (!metadata) {
    return undefined;
  }
  const extended = metadata as ScreenerItem["metadata"] & ScreenerMetadataExtras;
  return readString(extended[field]);
}
function screenerFundamentalData(
  item: ScreenerItem,
): Record<string, string | number | boolean | null | undefined> | undefined {
  const raw = item.fundamental_data;
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  return raw as Record<string, string | number | boolean | null | undefined>;
}

export function mapPickerHitToPortfolioProduct(
  hit: ProductPickerHit,
): PortfolioHoldingProduct {
  return {
    resource_id:
      hit.child_universe_id ??
      hit.isin ??
      hit.morningstar_reference_id ??
      hit.display_name,
    child_universe_id: hit.child_universe_id ?? undefined,
    display_name: hit.display_name,
    long_name: hit.display_name,
    resource_type: "product",
    currency: hit.currency ?? undefined,
    isin: hit.isin ?? undefined,
    pickerHit: hit,
  };
}

export function mapScreenerItemToPortfolioProduct(
  item: ScreenerItem,
): PortfolioHoldingProduct {
  const resourceId =
    item.metadata?.resource_id ?? item.id ?? item.resource_id ?? "";
  const isPortfolio = item.resource_type === "portfolio";
  const data = item.data;
  const fundamental = screenerFundamentalData(item);
  const longName =
    readString(data?.long_name) ??
    readString(data?.portfolio_name) ??
    readString(fundamental?.long_name) ??
    readMetadataString(item.metadata, "long_name") ??
    readString(item.long_name);

  return {
    resource_id: resourceId,
    display_name: item.display_name ?? item.name ?? resourceId,
    long_name: longName,
    resource_type: item.resource_type ?? "product",
    company_id: readMetadataString(item.metadata, "company_id"),
    currency: readString(item.currency) ?? readString(data?.currency),
    default_performance_stream: item.default_stream_type,
    isin: readString(data?.isin) ?? readString(fundamental?.isin),
    child_universe_id: isPortfolio ? undefined : resourceId,
    portfolio_reference_id: isPortfolio ? resourceId : undefined,
  };
}

export function portfolioProductResultKey(
  product: PortfolioHoldingProduct,
): string {
  if (product.pickerHit) {
    return (
      product.pickerHit.child_universe_id ??
      product.pickerHit.morningstar_reference_id ??
      product.pickerHit.isin ??
      product.display_name
    );
  }
  return (
    product.child_universe_id ??
    product.portfolio_reference_id ??
    product.resource_id ??
    product.display_name
  );
}
